import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummarySection } from "./SummarySection";

const FIELD_KEYS = [
  "ProcedureTypeSet",
  "TenderValidityMonthsSet",
  "SubmissionLanguagesSet",
  "SubmissionLanguagesValid",
] as const satisfies readonly KnownFieldKey[];

const LANGUAGE_LABELS: Record<string, string> = {
  NOR: "Norwegian",
  ENG: "English",
};

interface ProcedureSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const ProcedureSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: ProcedureSectionProps) => {
  const { t } = useTranslation();
  const { publicContext } = event;
  const validityMonths = publicContext.eFormsNoticeData?.tenderValidityMonths;
  const submissionLanguages = publicContext.eFormsNoticeData?.submissionLanguages ?? [];

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Procedure")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryFieldGroup>
        <SummaryField
          label={t("Procedure type")}
          value={
            publicContext.procedureType
              ? t(publicContext.procedureType)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ProcedureTypeSet")}
        />
        <SummaryField
          label={t("Tender validity period")}
          value={
            validityMonths != null
              ? `${validityMonths} ${t("month", { count: validityMonths })}`
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("TenderValidityMonthsSet")}
        />
        <SummaryField
          label={t("Submission language")}
          value={
            submissionLanguages.length > 0
              ? submissionLanguages.map((code) => t(LANGUAGE_LABELS[code] ?? code)).join(", ")
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("SubmissionLanguagesSet")}
          extraStatuses={[readinessFor("SubmissionLanguagesValid")]}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
